import {
  Alert,
  Box,
  ButtonGroup,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Snackbar,
  Typography,
  useTheme,
} from '@mui/material';
import { useCreateFileUrl, useUpdateAdmin, useUpdateTherapist } from '_api/index';
import { AdminPayload, Therapist, TherapistPayload } from '_api/types';
import { GreyButton, PrimaryButton } from 'components/atoms';
import { FileExtention, FILE_EXTENSION_TO_MIME_MAP } from 'components/molecules/types';
import React, { ChangeEvent, useState } from 'react';
import { IoMdClose } from 'react-icons/io';
import { uploadFile, md5Base64, generateFileChallenge } from 'utils/media';

import { UpdatePhotoModalProps } from './types';

export const UploadPhotoModal = ({ account, open, onClose }: UpdatePhotoModalProps) => {
  const theme = useTheme();
  const { mutateAsync: createUploadUrl } = useCreateFileUrl();
  const { mutateAsync: updateTherapist } = useUpdateTherapist();
  const { mutateAsync: updateAdmin } = useUpdateAdmin();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [selectedFileType, setSelectedFileType] = useState<FileExtention | undefined>(
    undefined,
  );
  const [showError, setShowError] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  const getFileType = (ext: string) => {
    const fileType = ext.split('.').pop();
    const mimeType = FILE_EXTENSION_TO_MIME_MAP[fileType || ''];
    if (mimeType) {
      setSelectedFileType(mimeType);
    } else {
      setShowError(true);
    }
  };

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (event && event.target && event.target.files && event.target.files.length > 0) {
      const file = event.target.files[0];
      setSelectedFile(file);
      getFileType(file.name);
    }
  };

  const onUploadSuccess = async (imageUrl: string) => {
    if (account.accountType === 'THERAPIST') {
      const {
        accountType: _accountType,
        fullImageUrl: _fullImageUrl,
        hospital: _hospital,
        status: _status,
        therapistSpecializations: _therapistSpecializations,
        ...rest
      } = account as Therapist;
      const payload = {
        ...rest,
        imageUrl: imageUrl,
        hospitalId: _hospital?.id,
        therapistSpecializations: _therapistSpecializations.map((x) => x.name),
      } as TherapistPayload;
      await updateTherapist(payload);
      setShowSuccess(true);
      onClose();
    } else if (account.accountType === 'ADMIN') {
      const { imageUrl: _imageUrl, ...rest } = account;
      const payload = {
        ...rest,
        imageUrl: imageUrl,
      } as AdminPayload;
      await updateAdmin(payload);
      setShowSuccess(true);
      onClose();
    }
  };

  const handleUploadClick = async () => {
    if (selectedFile && selectedFileType) {
      const checksum = await md5Base64(selectedFile);
      const challenge = await generateFileChallenge(selectedFile);
      const uploadUrl = await createUploadUrl({
        contentType: selectedFileType,
        filename: selectedFile.name,
        checksum,
        challenge,
      });

      try {
        await uploadFile(uploadUrl.url, selectedFile, selectedFileType);
        await onUploadSuccess(uploadUrl.key);
      } catch (e) {
        setShowError(true);
      }
    }
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle
        sx={{
          // @ts-ignore
          backgroundColor: `${theme.palette.grey.light}`,
          display: 'flex',
          justifyContent: 'space-between',
          p: 2,
        }}
      >
        <Box sx={{ display: 'flex', padding: '5px' }}>
          <Typography
            fontSize={'24px'}
            fontWeight={700}
            component="div"
            sx={{ marginTop: 'auto', marginBottom: 'auto' }}
          >
            Upload Photo
          </Typography>
        </Box>
        <IconButton aria-label="close" onClick={onClose} sx={{ marginRight: 5 }}>
          <IoMdClose />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <input
          type="file"
          accept="image/*"
          onChange={handleFileChange}
          style={{ padding: '10px 0' }}
        />
      </DialogContent>
      <DialogActions sx={{ margin: 'auto' }}>
        <ButtonGroup size="large" variant="contained">
          <GreyButton
            aria-label="close"
            onClick={onClose}
            sx={{ paddingLeft: '48px', paddingRight: '48px' }}
          >
            <IoMdClose style={{ fontSize: '2rem' }} />
          </GreyButton>
          <PrimaryButton
            aria-label="close"
            onClick={handleUploadClick}
            sx={{ paddingLeft: '48px', paddingRight: '48px' }}
          >
            SUBMIT
          </PrimaryButton>
        </ButtonGroup>
      </DialogActions>
      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        autoHideDuration={5000}
        onClose={() => setShowError(false)}
        open={showError}
      >
        <Alert onClose={() => setShowError(false)} severity="error" variant="filled">
          Error uploaing file, please try again.
        </Alert>
      </Snackbar>
      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        autoHideDuration={5000}
        onClose={() => setShowSuccess(false)}
        open={showSuccess}
      >
        <Alert onClose={() => setShowSuccess(false)} severity="success" variant="filled">
          File successfully uploaded.
        </Alert>
      </Snackbar>
    </Dialog>
  );
};
